import '@patron/patron-css/patron/index.css'
import React, { useState, useEffect } from "react"
import PropTypes from 'prop-types'

// Application components
import EnterpriseRegistrationForm from './EnterpriseRegistrationForm'

// Constants and utilities
import * as constants from '../../constants/Constants'

// Type definitions
import { EnterpriseDetailsPropType } from './propTypes'

// Styles
import './EnterpriseRegistration.scss'

// Helper function to extract contact info from enterprise details
const extractContactInfo = (enterpriseDetails) => {
    if (!enterpriseDetails?.contactInfo) {
        return {};
    }
    
    const contactInfo = enterpriseDetails.contactInfo;
    return {
        dataProtectionOfficerName: contactInfo.dataProtectionOfficerName || '',
        dataProtectionOfficerEmail: contactInfo.dataProtectionOfficerEmail || '',
        dataProtectionOfficerPhone: contactInfo.dataProtectionOfficerPhone || '',
        euRepresentativeName: contactInfo.euRepresentativeName || '',
        euRepresentativeEmail: contactInfo.euRepresentativeEmail || '',
        euRepresentativePhone: contactInfo.euRepresentativePhone || '',
        contactEmail: contactInfo.contactEmail || ''
    };
};

// Helper function to update form data with enterprise response data
const getUpdatedFormData = (prevData, enterpriseResponse) => {
    const updatedData = { ...prevData };
    
    // Update display name if available
    if (enterpriseResponse.enterpriseDisplayName) {
        updatedData.enterpriseDisplayName = enterpriseResponse.enterpriseDisplayName;
    }
    
    // Extract and update contact info if available
    if (enterpriseResponse.enterpriseDetails) {
        const contactInfo = extractContactInfo(enterpriseResponse.enterpriseDetails);
        return { ...updatedData, ...contactInfo };
    }
    
    return updatedData;
};

const EMMManaged = ({ 
    enterpriseResponse, 
    updateFormData, 
    updateFormValidity, 
    showSpinner, 
    showOrHideButton, 
    registerEnterprise 
}) => {
    // Translation will be handled by the shared form component
    const [formData, setFormData] = useState({
        enterpriseDisplayName: '',
        dataProtectionOfficerName: '',
        dataProtectionOfficerEmail: '',
        dataProtectionOfficerPhone: '',
        euRepresentativeName: '',
        euRepresentativeEmail: '',
        euRepresentativePhone: '',
        contactEmail: '',
        termsAndConditions: false
    });

    useEffect(() => {
        if (enterpriseResponse.enterpriseId !== '') {
            setFormData(prevData => getUpdatedFormData(prevData, enterpriseResponse));
        }
    }, [enterpriseResponse]);

    // Handle form data changes from the shared form
    const handleFormDataChange = (field, value) => {
        // Update local state
        setFormData(prevData => ({
            ...prevData,
            [field]: value
        }));
        if (updateFormData) {
            updateFormData(field, value);
        }
    };
    
    // Handle form validity changes
    const handleFormValidityChange = (field, value) => {
        if (updateFormValidity) {
            updateFormValidity(field, value);
        }
    };

    // Handle form submission
    const handleFormSubmit = (method, data) => {
        registerEnterprise?.(method, data);
    };

    return (
        <EnterpriseRegistrationForm
            enterpriseResponse={enterpriseResponse}
            initialFormData={formData}
            registrationType={constants.ENTERPRISE_MANAGED_TYPE}
            showSpinner={showSpinner}
            showOrHideButton={showOrHideButton}
            onFormDataChange={handleFormDataChange}
            onFormValidityChange={handleFormValidityChange}
            onSubmit={handleFormSubmit}
            showTermsCheckbox={true}
        />
    );
};

EMMManaged.propTypes = {
    /**
     * The enterprise response object
     * @see EnterpriseResponse in models.js
     */
    enterpriseResponse: PropTypes.shape({
        enterpriseId: PropTypes.string.isRequired,
        enterpriseDisplayName: PropTypes.string,
        accountType: PropTypes.string,
        serviceAccount: PropTypes.string,
        enterpriseDetails: PropTypes.oneOfType([
            PropTypes.string,
            EnterpriseDetailsPropType
        ]),
        hasToken: PropTypes.bool
    }),
    updateFormData: PropTypes.func.isRequired,
    updateFormValidity: PropTypes.func.isRequired,
    showSpinner: PropTypes.bool,
    showOrHideButton: PropTypes.bool,
    registerEnterprise: PropTypes.func.isRequired
};

export default EMMManaged;
